import {
    Box,
    Heading,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Card,
    CardBody,
    Flex,
    Select,
    Badge,
    Text,
    Icon,
    HStack,
    Spinner,
    Center,
    RangeSlider,
    RangeSliderTrack,
    RangeSliderFilledTrack,
    RangeSliderThumb,
    Collapse,
    useDisclosure,
    Checkbox,
    useToast,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerCloseButton,
    VStack,
    FormControl,
    FormLabel,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MdSearch, MdFilterList, MdAdd, MdDownload, MdExpandLess } from 'react-icons/md';


import { useSearch, usePriceSets, useAddLineItem } from '@/services/hooks';

export function Search() {
    const toast = useToast();
    const [searchParams] = useSearchParams();
    const { isOpen: filtersOpen, onToggle: toggleFilters } = useDisclosure();
    const { isOpen: drawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();

    const initialQ = searchParams.get('q') || '';
    const [query, setQuery] = useState(initialQ);
    const [searchQuery, setSearchQuery] = useState(initialQ);

    // Sync with URL params
    useEffect(() => {
        const q = searchParams.get('q') || '';
        setQuery(q);
        setSearchQuery(q);
    }, [searchParams]);

    const [filters, setFilters] = useState({
        entity_type: '',
        agency: '',
        uom: '',
        min_price: 0,
        max_price: 10000,
    });
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [selectedPriceSet, setSelectedPriceSet] = useState<number | null>(null);

    const { data, isLoading, isError } = useSearch(searchQuery, filters);
    const { data: priceSets } = usePriceSets();
    const addItemsMutation = useAddLineItem();

    const handleSearch = () => {
        if (query.length >= 2) {
            setSearchQuery(query);
        }
    };

    const toggleItem = (id: number) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleAddToSet = async () => {
        if (!selectedPriceSet || selectedItems.length === 0) return;

        const selectedResults = results.filter((r: any) => selectedItems.includes(r.id));
        try {
            // Add each selected result as a new line item in the price set
            await Promise.all(
                selectedResults.map((item: any) =>
                    addItemsMutation.mutateAsync({
                        setId: selectedPriceSet,
                        data: {
                            name: item.title || item.description,
                            description: item.description,
                            uom: item.uom || 'EA',
                            unit_price: item.unit_price || 0,
                        },
                    })
                )
            );
            toast({
                title: `${selectedResults.length} item${selectedResults.length > 1 ? 's' : ''} added to price set`,
                status: 'success',
                duration: 3000,
            });
            setSelectedItems([]);
            closeDrawer();
        } catch (error) {
            toast({
                title: 'Failed to add items',
                status: 'error',
                duration: 3000,
            });
        }
    };


    const results = data?.results || [];

    return (
        <Box>
            <Heading size="lg" mb={6}>
                Search
            </Heading>

            {/* Search Bar */}
            <Card mb={6}>
                <CardBody>
                    <Flex gap={4} flexWrap="wrap" mb={4}>
                        <InputGroup flex="1" minW="300px">
                            <InputLeftElement>
                                <Icon as={MdSearch} color="gray.500" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search bid items by description, item code, or vendor..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                        </InputGroup>
                        <Button
                            leftIcon={filtersOpen ? <MdExpandLess /> : <MdFilterList />}
                            variant="outline"
                            onClick={toggleFilters}
                        >
                            Filters
                        </Button>
                        <Button onClick={handleSearch} isLoading={isLoading} colorScheme="brand">
                            Search
                        </Button>
                    </Flex>

                    {/* Filter Panel */}
                    <Collapse in={filtersOpen}>
                        <Box pt={4} borderTop="1px solid" borderColor="navy.600">
                            <Flex gap={4} flexWrap="wrap">
                                <FormControl maxW="200px">
                                    <FormLabel fontSize="sm">Entity Type</FormLabel>
                                    <Select
                                        size="sm"
                                        placeholder="All"
                                        value={filters.entity_type}
                                        onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                                    >
                                        <option value="bid_item">Bid Items</option>
                                        <option value="project">Projects</option>
                                    </Select>
                                </FormControl>

                                <FormControl maxW="200px">
                                    <FormLabel fontSize="sm">Agency</FormLabel>
                                    <Select
                                        size="sm"
                                        placeholder="All"
                                        value={filters.agency}
                                        onChange={(e) => setFilters({ ...filters, agency: e.target.value })}
                                    >
                                        <option value="Chicago DPS">Chicago DPS</option>
                                        <option value="Cook County">Cook County</option>
                                        <option value="Illinois State">Illinois State</option>
                                    </Select>
                                </FormControl>

                                <FormControl maxW="200px">
                                    <FormLabel fontSize="sm">Unit of Measure</FormLabel>
                                    <Select
                                        size="sm"
                                        placeholder="All"
                                        value={filters.uom}
                                        onChange={(e) => setFilters({ ...filters, uom: e.target.value })}
                                    >
                                        <option value="SF">SF - Square Feet</option>
                                        <option value="LF">LF - Linear Feet</option>
                                        <option value="CY">CY - Cubic Yards</option>
                                        <option value="SY">SY - Square Yards</option>
                                        <option value="TON">TON</option>
                                        <option value="EA">EA - Each</option>
                                        <option value="LS">LS - Lump Sum</option>
                                    </Select>
                                </FormControl>

                                <FormControl maxW="250px">
                                    <FormLabel fontSize="sm">
                                        Price Range: ${filters.min_price} - ${filters.max_price.toLocaleString()}
                                    </FormLabel>
                                    <RangeSlider
                                        min={0}
                                        max={10000}
                                        step={100}
                                        value={[filters.min_price, filters.max_price]}
                                        onChange={([min, max]) => setFilters({ ...filters, min_price: min, max_price: max })}
                                        colorScheme="brand"
                                    >
                                        <RangeSliderTrack>
                                            <RangeSliderFilledTrack />
                                        </RangeSliderTrack>
                                        <RangeSliderThumb index={0} />
                                        <RangeSliderThumb index={1} />
                                    </RangeSlider>
                                </FormControl>
                            </Flex>
                        </Box>
                    </Collapse>
                </CardBody>
            </Card>

            {/* Results Actions */}
            {selectedItems.length > 0 && (
                <Card mb={4} bg="brand.500">
                    <CardBody py={3}>
                        <Flex justify="space-between" align="center">
                            <Text fontWeight="medium">
                                {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                            </Text>
                            <HStack>
                                <Button size="sm" variant="ghost" onClick={() => setSelectedItems([])}>
                                    Clear
                                </Button>
                                <Button size="sm" leftIcon={<MdAdd />} onClick={openDrawer}>
                                    Add to Price Set
                                </Button>
                                <Button size="sm" leftIcon={<MdDownload />}>
                                    Export
                                </Button>
                            </HStack>
                        </Flex>
                    </CardBody>
                </Card>
            )}

            {/* Results */}
            <Card>
                <CardBody p={0}>
                    {isLoading ? (
                        <Center py={12}>
                            <Spinner size="lg" color="brand.500" />
                        </Center>
                    ) : isError ? (
                        <Center py={12}>
                            <Text color="red.400">Error loading results. Please try again.</Text>
                        </Center>
                    ) : results.length === 0 ? (
                        <Center py={12}>
                            <Text color="gray.500">
                                {searchQuery ? 'No results found.' : 'Enter a search query to find bid items.'}
                            </Text>
                        </Center>
                    ) : (
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th w="40px"></Th>
                                    <Th>Description</Th>
                                    <Th>Project</Th>
                                    <Th isNumeric>Unit Price</Th>
                                    <Th>UOM</Th>
                                    <Th>Agency</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {results.map((item: any) => (
                                    <Tr key={`${item.entity_type}-${item.id}`} _hover={{ bg: 'navy.700' }}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedItems.includes(item.id)}
                                                onChange={() => toggleItem(item.id)}
                                                colorScheme="brand"
                                            />
                                        </Td>
                                        <Td>
                                            <Text fontWeight="medium">{item.title}</Text>
                                            <Text fontSize="sm" color="gray.500" noOfLines={1}>
                                                {item.description}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">{item.project_title || '-'}</Text>
                                        </Td>
                                        <Td isNumeric fontWeight="semibold">
                                            {item.unit_price
                                                ? `$${item.unit_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                                : '-'}
                                        </Td>
                                        <Td>
                                            <Badge colorScheme="brand">{item.uom || '-'}</Badge>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">{item.agency || '-'}</Text>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Add to Price Set Drawer */}
            <Drawer isOpen={drawerOpen} onClose={closeDrawer} placement="right" size="md">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>Add to Price Set</DrawerHeader>
                    <DrawerBody>
                        <VStack align="stretch" spacing={4}>
                            <Text color="gray.400">
                                Select a price set to add {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}.
                            </Text>

                            <FormControl>
                                <FormLabel>Price Set</FormLabel>
                                <Select
                                    placeholder="Select price set..."
                                    value={selectedPriceSet || ''}
                                    onChange={(e) => setSelectedPriceSet(Number(e.target.value))}
                                >
                                    {(priceSets || []).map((set: any) => (
                                        <option key={set.id} value={set.id}>
                                            {set.name} ({set.items_count} items)
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                colorScheme="brand"
                                onClick={handleAddToSet}
                                isLoading={addItemsMutation.isPending}
                                isDisabled={!selectedPriceSet}
                            >
                                Add Items
                            </Button>
                        </VStack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    );
}
